/** Ids a snapshot no longer carries. */
const vanished = (table, upserts) => {
    const kept = new Set((upserts ?? []).map((r) => r.id));
    return [...table.keys()].filter((id) => !kept.has(id));
};
const narrowed = (cb, filters) => (delta) => {
    const upserts = delta.upserts?.filter((p) => p.type === filters.type);
    const out = {
        ...(upserts?.length ? { upserts } : {}),
        ...(delta.removes?.length ? { removes: delta.removes } : {}),
        ...(delta.snapshot ? { snapshot: true } : {}),
    };
    if (out.upserts || out.removes || out.snapshot)
        cb(out);
};
/**
 * @internal Backs `artis.v1.pages`: the confirmed snapshot pushed by the worker plus the
 * optimistic overlays of commands still in flight, merged into one delta stream with
 * catch-up for late subscribers. The worker bridge calls `apply`; the command layer calls
 * `begin` / `commit` / `rollback`; consumers subscribe through the `v1.pages` surface.
 */
export class PagesStore {
    pages = new Map();
    columns = new Map();
    pending = new Map();
    pageSubs = new Set();
    columnSubs = new Map();
    /** Set once the worker's full list for that table has been applied. Catch-up after that carries `snapshot`. */
    pagesSnapshot = false;
    columnsSnapshot = false;
    apply = (delta) => {
        if (delta.pages) {
            const removes = [
                ...(delta.pages.removes ?? []),
                ...(delta.snapshot ? vanished(this.pages, delta.pages.upserts) : []),
            ];
            const touched = [...(delta.pages.upserts ?? []).map((p) => p.id), ...removes];
            for (const p of delta.pages.upserts ?? [])
                this.pages.set(p.id, p);
            for (const id of removes)
                this.pages.delete(id);
            this.markEchoed("pages", touched);
            if (delta.snapshot)
                this.pagesSnapshot = true;
            this.publishPages(touched, !!delta.snapshot);
        }
        if (delta.columns) {
            const removes = [
                ...(delta.columns.removes ?? []),
                ...(delta.snapshot ? vanished(this.columns, delta.columns.upserts) : []),
            ];
            const touched = [...(delta.columns.upserts ?? []).map((c) => c.id), ...removes];
            const pageIds = this.columnPages(touched);
            for (const c of delta.columns.upserts ?? [])
                this.columns.set(c.id, c);
            for (const id of removes)
                this.columns.delete(id);
            this.markEchoed("columns", touched);
            if (delta.snapshot)
                this.columnsSnapshot = true;
            this.publishColumns(touched, pageIds, !!delta.snapshot);
        }
    };
    /** The merged view of one page, for command results. */
    getPage = (id) => this.mergedPage(id);
    subscribe = (cb, filters) => {
        const listener = filters?.type === undefined ? cb : narrowed(cb, filters);
        this.pageSubs.add(listener);
        const upserts = [...this.pages.keys()].flatMap((id) => {
            const p = this.mergedPage(id);
            return p ? [p] : [];
        });
        const created = this.pendingOnly("pages", (id) => this.mergedPage(id));
        const all = [...upserts, ...created];
        if (this.pagesSnapshot || all.length) {
            listener({
                ...(all.length ? { upserts: all } : {}),
                ...(this.pagesSnapshot ? { snapshot: true } : {}),
            });
        }
        return () => void this.pageSubs.delete(listener);
    };
    subscribeColumns = (pageId, cb) => {
        const subs = this.columnSubs.get(pageId) ?? new Set();
        subs.add(cb);
        this.columnSubs.set(pageId, subs);
        const ids = new Set([...this.columns.keys(), ...this.pendingOnlyIds("columns")]);
        const upserts = [...ids].flatMap((id) => {
            const c = this.mergedColumn(id);
            return c && c.pageId === pageId ? [c] : [];
        });
        if (this.columnsSnapshot || upserts.length) {
            cb({
                ...(upserts.length ? { upserts } : {}),
                ...(this.columnsSnapshot ? { snapshot: true } : {}),
            });
        }
        return () => {
            subs.delete(cb);
            if (subs.size === 0)
                this.columnSubs.delete(pageId);
        };
    };
    begin = (token, local) => {
        const overlay = { pages: new Map(), columns: new Map(), echoed: new Set() };
        for (const p of local.pages?.upserts ?? [])
            overlay.pages.set(p.id, p);
        for (const id of local.pages?.removes ?? [])
            overlay.pages.set(id, null);
        const columnIds = [...(local.columns?.upserts ?? []).map((c) => c.id), ...(local.columns?.removes ?? [])];
        const pageIds = this.columnPages(columnIds);
        for (const c of local.columns?.upserts ?? [])
            overlay.columns.set(c.id, c);
        for (const id of local.columns?.removes ?? [])
            overlay.columns.set(id, null);
        this.pending.set(token, overlay);
        this.publishPages([...overlay.pages.keys()]);
        this.publishColumns(columnIds, pageIds);
    };
    commit = (token) => {
        const overlay = this.pending.get(token);
        if (!overlay)
            return;
        for (const [id, p] of overlay.pages) {
            if (overlay.echoed.has(`pages:${id}`))
                continue;
            if (p === null)
                this.pages.delete(id);
            else
                this.pages.set(id, { ...this.pages.get(id), ...p });
        }
        for (const [id, c] of overlay.columns) {
            if (overlay.echoed.has(`columns:${id}`))
                continue;
            if (c === null)
                this.columns.delete(id);
            else
                this.columns.set(id, { ...this.columns.get(id), ...c });
        }
        this.pending.delete(token);
    };
    rollback = (token) => {
        const overlay = this.pending.get(token);
        if (!overlay)
            return;
        const columnIds = [...overlay.columns.keys()];
        const pageIds = this.columnPages(columnIds);
        this.pending.delete(token);
        this.publishPages([...overlay.pages.keys()]);
        this.publishColumns(columnIds, pageIds);
    };
    markEchoed = (kind, ids) => {
        for (const overlay of this.pending.values()) {
            for (const id of ids)
                if (overlay[kind].has(id))
                    overlay.echoed.add(`${kind}:${id}`);
        }
    };
    mergedPage = (id) => this.merged("pages", id, this.pages);
    mergedColumn = (id) => this.merged("columns", id, this.columns);
    /** Confirmed row with every pending patch folded in, in command order; a pending create is the patch itself. */
    merged(kind, id, confirmed) {
        let value = confirmed.get(id);
        for (const overlay of this.pending.values()) {
            const table = overlay[kind];
            if (!table.has(id))
                continue;
            const local = table.get(id);
            value = local === null ? undefined : { ...value, ...local };
        }
        return value;
    }
    pendingOnlyIds = (kind) => {
        const confirmed = kind === "pages" ? this.pages : this.columns;
        const ids = new Set();
        for (const overlay of this.pending.values()) {
            for (const id of overlay[kind].keys())
                if (!confirmed.has(id))
                    ids.add(id);
        }
        return [...ids];
    };
    pendingOnly(kind, read) {
        return this.pendingOnlyIds(kind).flatMap((id) => {
            const v = read(id);
            return v ? [v] : [];
        });
    }
    /** The page each column id belongs to, read before a change so removes can still be routed. */
    columnPages = (ids) => {
        const out = new Map();
        for (const id of ids) {
            const pageId = this.mergedColumn(id)?.pageId;
            if (pageId)
                out.set(id, pageId);
        }
        return out;
    };
    publishPages = (ids, snapshot = false) => {
        if (this.pageSubs.size === 0)
            return;
        if (ids.length === 0 && !snapshot)
            return;
        const upserts = [];
        const removes = [];
        for (const id of new Set(ids)) {
            const p = this.mergedPage(id);
            if (p)
                upserts.push(p);
            else
                removes.push(id);
        }
        const delta = {
            ...(upserts.length ? { upserts } : {}),
            ...(removes.length ? { removes } : {}),
            ...(snapshot ? { snapshot: true } : {}),
        };
        if (!delta.upserts && !delta.removes && !delta.snapshot)
            return;
        for (const cb of this.pageSubs)
            cb(delta);
    };
    publishColumns = (ids, before, snapshot = false) => {
        if (this.columnSubs.size === 0)
            return;
        if (ids.length === 0 && !snapshot)
            return;
        const byPage = new Map();
        const bucket = (pageId) => {
            const b = byPage.get(pageId) ?? { upserts: [], removes: [] };
            byPage.set(pageId, b);
            return b;
        };
        for (const id of new Set(ids)) {
            const c = this.mergedColumn(id);
            const pageId = c?.pageId ?? before.get(id);
            if (!pageId)
                continue;
            if (c)
                bucket(pageId).upserts.push(c);
            else
                bucket(pageId).removes.push(id);
        }
        if (snapshot) {
            for (const pageId of this.columnSubs.keys()) {
                if (!byPage.has(pageId))
                    byPage.set(pageId, { upserts: [], removes: [] });
            }
        }
        for (const [pageId, { upserts, removes }] of byPage) {
            const subs = this.columnSubs.get(pageId);
            if (!subs)
                continue;
            const delta = {
                ...(upserts.length ? { upserts } : {}),
                ...(removes.length ? { removes } : {}),
                ...(snapshot ? { snapshot: true } : {}),
            };
            if (!delta.upserts && !delta.removes && !delta.snapshot)
                continue;
            for (const cb of subs)
                cb(delta);
        }
    };
}
//# sourceMappingURL=pagesStore.js.map